import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Save, Globe, Mail, Phone, MapPin, Clock, Facebook, Instagram, Youtube, BarChart, Lock, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSettings() {
  const location = useLocation();
  const navigate = useNavigate();
  const forcePasswordReset = location.state?.forcePasswordReset || false;
  
  const [settings, setSettings] = useState({
    church_name: 'Living Fruit Ministry',
    tagline: 'From a seed of faith, a tree of life grows',
    address: '109 N Beaumont Avenue #1078, Kissimmee, FL 34741',
    phone: '(407) 216-2853',
    email: 'livingfruitministry@gmail.com',
    service_times: 'Sunday Worship: 10:00 AM\nWednesday Bible Study: 7:00 PM\nSaturday Food Pantry: 9:00 AM - 12:00 PM',
    facebook_url: '',
    instagram_url: '',
    youtube_url: '',
    ga_tracking_id: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`, { withCredentials: true });
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings, { withCredentials: true });
      toast.success('Settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordData.new_password.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, { withCredentials: true });
      
      toast.success('Password changed successfully!');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      
      // If this was a forced reset, redirect to dashboard
      if (forcePasswordReset) {
        toast.success('You can now use the admin dashboard');
        navigate('/admin/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-settings">
      {/* Forced Password Reset Banner */}
      {forcePasswordReset && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 bg-amber-50 border-2 border-amber-400 rounded-xl flex items-start gap-3"
        >
          <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={24} />
          <div>
            <h3 className="font-semibold text-amber-800">Password Reset Required</h3>
            <p className="text-amber-700 text-sm">
              For security, please set a new password before accessing the dashboard. 
              Your current password is temporary.
            </p>
          </div>
        </motion.div>
      )}
      
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl text-charcoal">Site Settings</h1>
          <p className="text-charcoal/60 mt-1">Manage your church information</p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-burgundy hover:bg-purple text-white"
          data-testid="save-settings"
        >
          <Save size={18} className="mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Basic Info */}
        <div className="bg-white rounded-2xl p-6 border border-gold/10">
          <h2 className="font-serif text-lg text-charcoal mb-4 flex items-center gap-2">
            <Globe size={20} className="text-gold" />
            Basic Information
          </h2>
          
          <div className="space-y-4">
            <div>
              <Label>Church Name</Label>
              <Input
                value={settings.church_name}
                onChange={(e) => setSettings({...settings, church_name: e.target.value})}
                className="bg-cream border-gold/20"
                data-testid="settings-name"
              />
            </div>
            <div>
              <Label>Tagline</Label>
              <Input
                value={settings.tagline}
                onChange={(e) => setSettings({...settings, tagline: e.target.value})}
                className="bg-cream border-gold/20"
                data-testid="settings-tagline"
              />
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="bg-white rounded-2xl p-6 border border-gold/10">
          <h2 className="font-serif text-lg text-charcoal mb-4 flex items-center gap-2">
            <Mail size={20} className="text-gold" />
            Contact Information
          </h2>
          
          <div className="space-y-4">
            <div>
              <Label className="flex items-center gap-1"><MapPin size={14} /> Address</Label>
              <Input
                value={settings.address}
                onChange={(e) => setSettings({...settings, address: e.target.value})}
                className="bg-cream border-gold/20"
                data-testid="settings-address"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-1"><Phone size={14} /> Phone</Label>
                <Input
                  value={settings.phone}
                  onChange={(e) => setSettings({...settings, phone: e.target.value})}
                  className="bg-cream border-gold/20"
                  data-testid="settings-phone"
                />
              </div>
              <div>
                <Label className="flex items-center gap-1"><Mail size={14} /> Email</Label>
                <Input
                  value={settings.email}
                  onChange={(e) => setSettings({...settings, email: e.target.value})}
                  className="bg-cream border-gold/20"
                  data-testid="settings-email"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Service Times */}
        <div className="bg-white rounded-2xl p-6 border border-gold/10">
          <h2 className="font-serif text-lg text-charcoal mb-4 flex items-center gap-2">
            <Clock size={20} className="text-gold" />
            Service Times
          </h2>
          
          <div>
            <Label>Schedule (one per line)</Label>
            <Textarea
              value={settings.service_times}
              onChange={(e) => setSettings({...settings, service_times: e.target.value})}
              className="bg-cream border-gold/20"
              rows={4}
              data-testid="settings-times"
            />
          </div>
        </div>

        {/* Social Media */}
        <div className="bg-white rounded-2xl p-6 border border-gold/10">
          <h2 className="font-serif text-lg text-charcoal mb-4">Social Media</h2>
          
          <div className="space-y-4">
            <div>
              <Label className="flex items-center gap-1"><Facebook size={14} /> Facebook URL</Label>
              <Input
                value={settings.facebook_url}
                onChange={(e) => setSettings({...settings, facebook_url: e.target.value})}
                placeholder="https://facebook.com/..."
                className="bg-cream border-gold/20"
                data-testid="settings-facebook"
              />
            </div>
            <div>
              <Label className="flex items-center gap-1"><Instagram size={14} /> Instagram URL</Label>
              <Input
                value={settings.instagram_url}
                onChange={(e) => setSettings({...settings, instagram_url: e.target.value})}
                placeholder="https://instagram.com/..."
                className="bg-cream border-gold/20"
                data-testid="settings-instagram"
              />
            </div>
            <div>
              <Label className="flex items-center gap-1"><Youtube size={14} /> YouTube URL</Label>
              <Input
                value={settings.youtube_url}
                onChange={(e) => setSettings({...settings, youtube_url: e.target.value})}
                placeholder="https://youtube.com/..."
                className="bg-cream border-gold/20"
                data-testid="settings-youtube"
              />
            </div>
          </div>
        </div>

        {/* Analytics */}
        <div className="bg-white rounded-2xl p-6 border border-gold/10 lg:col-span-2">
          <h2 className="font-serif text-lg text-charcoal mb-4 flex items-center gap-2">
            <BarChart size={20} className="text-gold" />
            Analytics
          </h2>
          
          <div className="max-w-md">
            <Label>Google Analytics Tracking ID</Label>
            <Input
              value={settings.ga_tracking_id}
              onChange={(e) => setSettings({...settings, ga_tracking_id: e.target.value})}
              placeholder="G-XXXXXXXXXX or UA-XXXXXXXX-X"
              className="bg-cream border-gold/20"
              data-testid="settings-ga"
            />
            <p className="text-xs text-charcoal/50 mt-2">
              If provided, Google Analytics will be used. Otherwise, basic built-in analytics will track page views and form submissions.
            </p>
          </div>
        </div>

        {/* Change Password */}
        <div className="bg-white rounded-2xl p-6 border border-gold/10 lg:col-span-2">
          <h2 className="font-serif text-lg text-charcoal mb-4 flex items-center gap-2">
            <Lock size={20} className="text-gold" />
            Change Password
          </h2>
          
          <div className="grid md:grid-cols-3 gap-4 max-w-3xl">
            <div>
              <Label>Current Password</Label>
              <div className="relative">
                <Input
                  type={showPasswords.current ? 'text' : 'password'}
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                  placeholder="••••••••"
                  className="bg-cream border-gold/20 pr-10"
                  data-testid="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({...showPasswords, current: !showPasswords.current})}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-charcoal/40 hover:text-charcoal"
                >
                  {showPasswords.current ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            
            <div>
              <Label>New Password</Label>
              <div className="relative">
                <Input
                  type={showPasswords.new ? 'text' : 'password'}
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                  placeholder="••••••••"
                  className="bg-cream border-gold/20 pr-10"
                  data-testid="new-password-settings"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({...showPasswords, new: !showPasswords.new})}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-charcoal/40 hover:text-charcoal"
                >
                  {showPasswords.new ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            
            <div>
              <Label>Confirm New Password</Label>
              <div className="relative">
                <Input
                  type={showPasswords.confirm ? 'text' : 'password'}
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                  placeholder="••••••••"
                  className="bg-cream border-gold/20 pr-10"
                  data-testid="confirm-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({...showPasswords, confirm: !showPasswords.confirm})}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-charcoal/40 hover:text-charcoal"
                >
                  {showPasswords.confirm ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
          </div>
          
          <Button 
            onClick={handleChangePassword}
            disabled={changingPassword || !passwordData.current_password || !passwordData.new_password || !passwordData.confirm_password}
            className="mt-4 bg-burgundy hover:bg-purple text-white"
            data-testid="change-password-btn"
          >
            <Lock size={16} className="mr-2" />
            {changingPassword ? 'Changing...' : 'Change Password'}
          </Button>
        </div>
      </div>
    </div>
  );
}
